import React, { useState, useEffect } from 'react';
import { useNotifications } from '../../hooks/useNotifications';
import { apiClient } from '../../api/client';
import {
  trackPermissionRequested,
  trackPermissionResult,
  getAnalyticsSummary
} from '../../utils/notifications/notificationAnalytics';
import './ProfileNotificationSettings.css';

interface NotificationHistoryItem {
  id: string;
  type: string;
  title: string;
  message: string;
  url?: string;
  isRead: boolean;
  createdAt: string;
}

const ProfileNotificationSettings: React.FC = () => {
  const {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    error,
    requestPermission,
    subscribe,
    unsubscribe,
    updateNotificationPreferences,
    notificationPreferences
  } = useNotifications();
  
  const [preferences, setPreferences] = useState(notificationPreferences);
  const [notificationHistory, setNotificationHistory] = useState<NotificationHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  // Track which preference is currently being saved
  const [savingPreferences, setSavingPreferences] = useState<Record<string, boolean>>({});
  // Track save status for visual feedback
  const [saveStatus, setSaveStatus] = useState<Record<string, 'idle' | 'success' | 'error'>>({});
  
  // Fetch notification history
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setHistoryLoading(true);
        const data = await apiClient.notifications.getHistory();
        setNotificationHistory(data as NotificationHistoryItem[]);
      } catch (error) {
        console.error('Error fetching notification history:', error);
        // Set empty array on error to prevent UI crashes
        setNotificationHistory([]);
      } finally {
        setHistoryLoading(false);
      }
    };
    
    fetchHistory();
  }, []);
  
  // Update local state when preferences change
  useEffect(() => {
    setPreferences(notificationPreferences);
  }, [notificationPreferences]);
  
  // Handle permission request
  const handleRequestPermission = async () => {
    trackPermissionRequested();
    const newPermission = await requestPermission();
    trackPermissionResult(newPermission);
    
    if (newPermission === 'granted') {
      await subscribe();
    }
  };
  
  // Handle subscription toggle
  const handleSubscriptionToggle = async () => {
    if (isSubscribed) {
      await unsubscribe();
    } else {
      await subscribe();
    }
  };
  
  // Handle preference change with automatic saving
  const handlePreferenceChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    
    // Update local state
    const newPreferences = {
      ...preferences,
      [name]: checked
    };
    
    setPreferences(newPreferences);
    
    // Set saving state for this specific preference
    setSavingPreferences(prev => ({ ...prev, [name]: true }));
    setSaveStatus(prev => ({ ...prev, [name]: 'idle' }));
    
    try {
      // Save to server
      await updateNotificationPreferences(newPreferences);
      
      // Show success feedback
      setSaveStatus(prev => ({ ...prev, [name]: 'success' }));
      
      // Reset status after a delay
      setTimeout(() => {
        setSaveStatus(prev => ({ ...prev, [name]: 'idle' }));
      }, 2000);
    } catch (error) {
      // Show error feedback
      setSaveStatus(prev => ({ ...prev, [name]: 'error' }));
      
      // Revert local state on error
      setPreferences(notificationPreferences);
      
      console.error('Error saving notification preferences:', error);
    } finally {
      // Clear saving state
      setSavingPreferences(prev => ({ ...prev, [name]: false }));
    }
  };
  
  // Save feedback component
  const SaveFeedback: React.FC<{ status: 'idle' | 'success' | 'error' }> = ({ status }) => {
    if (status === 'idle') return null;
    
    return (
      <span className={`save-feedback ${status}`}>
        {status === 'success' && '✓ Saved'}
        {status === 'error' && '✗ Failed'}
      </span>
    );
  };
  
  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      await apiClient.notifications.markAllAsRead();
      
      // Update local state
      setNotificationHistory(prevHistory =>
        prevHistory.map(item => ({ ...item, isRead: true }))
      );
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };
  
  // Format notification time
  const formatNotificationTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.round(diffMs / 60000);
    const diffHours = Math.round(diffMs / 3600000);
    const diffDays = Math.round(diffMs / 86400000);
    
    if (diffMins < 60) {
      return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  };
  
  // Get notification icon based on type
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'transaction':
        return <span className="icon transaction-icon">💰</span>;
      case 'security':
        return <span className="icon security-icon">🔒</span>;
      case 'account':
        return <span className="icon account-icon">👤</span>;
      case 'marketing':
        return <span className="icon marketing-icon">📢</span>;
      default:
        return <span className="icon default-icon">📌</span>;
    }
  };
  
  // If push notifications are not supported
  if (!isSupported) {
    return (
      <div className="notification-settings">
        <h2>Notification Settings</h2>
        <div className="notification-alert">
          <p>Push notifications are not supported in your browser.</p>
          <p>You will still receive notifications when using the app.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="profile-section notification-settings">
      <h2>Notification Settings</h2>
      
      <div className="notification-settings-highlight">
        <p className="notification-importance">
          <span className="importance-icon">!</span>
          Enable notifications to receive critical updates about your transactions and account security.
        </p>
      </div>
      
      {error && (
        <div className="notification-error">
          <p>Error: {error.message}</p>
        </div>
      )}
      
      <div className="notification-permission">
        <h3>Push Notification Permission</h3>
        
        {permission === 'default' && (
          <div className="permission-request">
            <p>Allow VeriPay to send you notifications about your transactions and account?</p>
            <button 
              onClick={handleRequestPermission}
              disabled={isLoading}
              className="primary-button"
            >
              {isLoading ? 'Requesting...' : 'Allow Notifications'}
            </button>
          </div>
        )}
        
        {permission === 'granted' && (
          <div className="permission-granted">
            <p>
              <span className="status-icon">✓</span>
              Notifications are allowed for this device
            </p>
            <div className="preference-toggle main-toggle">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={isSubscribed}
                  onChange={handleSubscriptionToggle}
                  disabled={isLoading}
                />
                <span className="toggle-slider"></span>
              </label>
              <div className="toggle-label">
              <span className="toggle-title">Receive push notifications</span>
              {/* <span className="toggle-description">
                Receive notifications about your transactions and account
              </span> */}
            </div>
            </div>
          </div>
        )}
        
        {permission === 'denied' && (
          <div className="permission-denied">
            <p>
              <span className="status-icon">✗</span>
              Notifications are blocked for this device
            </p>
            <div className="browser-instructions">
              <p className="help-text">
                To enable notifications, you need to change your browser settings:
              </p>
              <div className="browser-steps">
                <h4>Chrome</h4>
                <ol>
                  <li>Click the lock icon in the address bar</li>
                  <li>Click "Site settings"</li>
                  <li>Find "Notifications" and change from "Block" to "Allow"</li>
                </ol>
                
                <h4>Firefox</h4>
                <ol>
                  <li>Click the lock icon in the address bar</li>
                  <li>Click the right arrow next to "Connection secure"</li>
                  <li>Click "More Information"</li>
                  <li>Go to "Permissions" tab and find "Send Notifications"</li>
                  <li>Change the setting to "Allow"</li>
                </ol>
                
                <h4>Safari</h4>
                <ol>
                  <li>Click Safari in the menu bar</li>
                  <li>Select "Settings for This Website..."</li>
                  <li>Find "Notifications" and select "Allow"</li>
                </ol>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {permission === 'granted' && (
        <div className="notification-preferences">
          <h3>Notification Preferences</h3>
          
          <div className="preference-toggle main-toggle">
            <label className="toggle-switch">
              <input
                type="checkbox"
                name="enabled"
                checked={preferences.enabled}
                onChange={handlePreferenceChange}
                disabled={isLoading || savingPreferences['enabled']}
              />
              <span className="toggle-slider">
                {savingPreferences['enabled'] && <span className="saving-indicator"></span>}
              </span>
            </label>
            <div className="toggle-label">
              <span className="toggle-title">Enable all notifications</span>
              <span className="toggle-description">
                Receive notifications about your transactions and account
              </span>
              <SaveFeedback status={saveStatus['enabled'] || 'idle'} />
            </div>
          </div>
          
          <div className={`preference-group ${!preferences.enabled ? 'disabled' : ''}`}>
            <div className="preference-toggle">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  name="transactionUpdates"
                  checked={preferences.transactionUpdates}
                  onChange={handlePreferenceChange}
                  disabled={isLoading || !preferences.enabled || savingPreferences['transactionUpdates']}
                />
                <span className="toggle-slider">
                  {savingPreferences['transactionUpdates'] && <span className="saving-indicator"></span>}
                </span>
              </label>
              <div className="toggle-label">
                <span className="toggle-title">Transaction updates</span>
                <span className="toggle-description">
                  Receive notifications when your transactions change status
                </span>
                <SaveFeedback status={saveStatus['transactionUpdates'] || 'idle'} />
              </div>
            </div>
            
            <div className="preference-toggle">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  name="securityAlerts"
                  checked={preferences.securityAlerts}
                  onChange={handlePreferenceChange}
                  disabled={isLoading || !preferences.enabled || savingPreferences['securityAlerts']}
                />
                <span className="toggle-slider">
                  {savingPreferences['securityAlerts'] && <span className="saving-indicator"></span>}
                </span>
              </label>
              <div className="toggle-label">
                <span className="toggle-title">Security alerts</span>
                <span className="toggle-description">
                  Receive notifications about security-related events
                </span>
                <SaveFeedback status={saveStatus['securityAlerts'] || 'idle'} />
              </div>
            </div>
            
            <div className="preference-toggle">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  name="accountUpdates"
                  checked={preferences.accountUpdates}
                  onChange={handlePreferenceChange}
                  disabled={isLoading || !preferences.enabled || savingPreferences['accountUpdates']}
                />
                <span className="toggle-slider">
                  {savingPreferences['accountUpdates'] && <span className="saving-indicator"></span>}
                </span>
              </label>
              <div className="toggle-label">
                <span className="toggle-title">Account updates</span>
                <span className="toggle-description">
                  Receive notifications about changes to your account
                </span>
                <SaveFeedback status={saveStatus['accountUpdates'] || 'idle'} />
              </div>
            </div>
            
            <div className="preference-toggle">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  name="marketingMessages"
                  checked={preferences.marketingMessages}
                  onChange={handlePreferenceChange}
                  disabled={isLoading || !preferences.enabled || savingPreferences['marketingMessages']}
                />
                <span className="toggle-slider">
                  {savingPreferences['marketingMessages'] && <span className="saving-indicator"></span>}
                </span>
              </label>
              <div className="toggle-label">
                <span className="toggle-title">Marketing messages</span>
                <span className="toggle-description">
                  Receive notifications about new features and promotions
                </span>
                <SaveFeedback status={saveStatus['marketingMessages'] || 'idle'} />
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* <div className="notification-history">
        <div className="history-header">
          <h3>Recent Notifications</h3>
          {notificationHistory.length > 0 && (
            <button 
              onClick={markAllAsRead}
              className="secondary-button"
            >
              Mark all as read
            </button>
          )}
        </div>
        
        {historyLoading ? (
          <div className="loading-spinner">Loading notification history...</div>
        ) : notificationHistory.length === 0 ? (
          <div className="empty-history">
            <p>You don't have any notifications yet</p>
          </div>
        ) : (
          <div className="history-list">
            {notificationHistory.map(notification => (
              <div 
                key={notification.id} 
                className={`history-item ${!notification.isRead ? 'unread' : ''}`}
              >
                <div className="notification-icon">
                  {getNotificationIcon(notification.type)}
                </div>
                <div className="notification-content">
                  <div className="notification-title">{notification.title}</div>
                  <div className="notification-message">{notification.message}</div>
                  <div className="notification-time">
                    {formatNotificationTime(notification.createdAt)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div> */}
      
      <div className="device-management">
        <h3>Notification Devices</h3>
        <p className="help-text">
          You're currently receiving notifications on this device.
          To manage other devices, visit each device and update settings there.
        </p>
      </div>
      
      {/* Admin-only section for analytics - would normally be hidden for regular users */}
      {process.env.NODE_ENV === 'development' && (
        <div className="notification-analytics">
          <h3>Notification Analytics</h3>
          <pre className="analytics-data">
            {getAnalyticsSummary()}
          </pre>
        </div>
      )}
    </div>
  );
};

export default ProfileNotificationSettings;